/* eslint-disable react/jsx-indent */
import { useState } from 'react';
import { CopyIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Code,
  Flex,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  Tbody,
  Td,
  Text,
  Tr,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { getAllActiveHttpsProviders } from '../../../redux/httpsProviders/actions';
import { getUserType } from '../../../redux/user/selectors';
import { pxToRem, UNVERIFY_PROVIDER, VERIFY_PROVIDER } from '../../../utils';
import { getFirebaseIdToken } from '../../../utils/utils';

export const ProviderDetails = ({ onClose, isOpen, providerDetail }: any) => {
  const [isLoading, setIsLoading] = useState(false);

  const toast = useToast();
  const dispatch = useAppDispatch();
  const userType = useAppSelector(getUserType);

  const verifyProvider = async () => {
    try {
      const token = await getFirebaseIdToken();
      setIsLoading(true);
      const res = await fetch(VERIFY_PROVIDER(providerDetail.httpProviderId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      //dispatch(getAllActiveHttpsProviders())

      if (res.status === 200) {
        setIsLoading(false);
        dispatch(getAllActiveHttpsProviders());
        toast({
          title: 'Provider Verified',
          description: 'Provider has been verified successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        onClose();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Some error occured while verifying provider',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const unverifyProvider = async () => {
    try {
      const token = await getFirebaseIdToken();
      setIsLoading(true);
      const res = await fetch(UNVERIFY_PROVIDER(providerDetail.httpProviderId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (res.status === 200) {
        setIsLoading(false);
        dispatch(getAllActiveHttpsProviders());
        toast({
          title: 'Provider Unverified',
          description: 'Provider has been unverified successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        onClose();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Some error occured while unverifying provider',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    toast({
      title: 'Copied to clipboard',
      status: 'success',
      duration: 2000,
      isClosable: true,
      position: 'top-right',
    });
  };

  console.log({ providerDetail });

  const renderRequestPanel = (request: any, index: number) => (
    <TabPanel key={index}>
      <VStack align="stretch" spacing={4} w="full">
        <Box borderWidth="1px" borderRadius="lg" p={4}>
          <Text fontWeight="bold" mb={2}>
            Request Details
          </Text>
          <Table variant="simple" size="sm">
            <Tbody>
              <Tr>
                <Td fontWeight="medium">URL</Td>
                <Td>{request.url}</Td>
              </Tr>
              <Tr>
                <Td fontWeight="medium">Method</Td>
                <Td>
                  <Tag colorScheme="blue">{request.method}</Tag>
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="medium">URL Type</Td>
                <Td>{request.urlType}</Td>
              </Tr>
              <Tr>
                <Td fontWeight="medium">Request Hash</Td>
                <Td>
                  <Flex align="center">
                    <Text fontSize="sm" mr={2}>
                      {request.requestHash}
                    </Text>
                    <IconButton
                      variant="ghost"
                      aria-label="Copy provider request hash"
                      icon={<CopyIcon />}
                      size="20px"
                      onClick={() => handleCopy(request.requestHash)}
                    />
                  </Flex>
                </Td>
              </Tr>
            </Tbody>
          </Table>
        </Box>

        <Box borderWidth="1px" borderRadius="lg" p={4}>
          <Text fontWeight="bold" mb={2}>
            Body Sniff Configuration
          </Text>
          <Table variant="simple" size="sm">
            <Tbody>
              <Tr>
                <Td fontWeight="medium">Status</Td>
                <Td>
                  <Tag colorScheme={request.bodySniff.enabled ? 'green' : 'red'}>
                    {request.bodySniff.enabled ? 'Enabled' : 'Disabled'}
                  </Tag>
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="medium">Regex</Td>
                <Td>
                  <Code p={2}>{request.bodySniff.regex || 'No regex defined'}</Code>
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="medium">Template</Td>
                <Td>
                  <Code p={2}>{request.bodySniff.template || 'No template defined'}</Code>
                </Td>
              </Tr>
            </Tbody>
          </Table>
        </Box>

        <Box borderWidth="1px" borderRadius="lg" p={4}>
          <Text fontWeight="bold" mb={2}>
            Response Matches
          </Text>
          <VStack align="stretch" spacing={2}>
            {request.responseMatches.map((match: any, idx: number) => (
              <Box key={idx} p={2} borderWidth="1px" borderRadius="md">
                <Flex justify="space-between" align="center">
                  <Text fontSize="sm" fontWeight="medium">
                    Value:
                  </Text>
                  <Code p={1}>{match.value}</Code>
                </Flex>
                <Flex justify="space-between" align="center" mt={2}>
                  <Text fontSize="sm" fontWeight="medium">
                    Type:
                  </Text>
                  <Tag size="sm">{match.type}</Tag>
                </Flex>
              </Box>
            ))}
          </VStack>
        </Box>

        <Box borderWidth="1px" borderRadius="lg" p={4}>
          <Text fontWeight="bold" mb={2}>
            Response Redactions
          </Text>
          <VStack align="stretch" spacing={2}>
            {request.responseRedactions.map((redaction: any, idx: number) => (
              <Box key={idx} p={2} borderWidth="1px" borderRadius="md">
                <VStack align="stretch" spacing={1}>
                  {redaction?.jsonPath?.length > 0 && (
                    <Flex justify="space-between">
                      <Text fontSize="sm" fontWeight="medium">
                        JSON Path:
                      </Text>
                      <Code p={1}>{redaction.jsonPath}</Code>
                    </Flex>
                  )}
                  {redaction?.xPath?.length > 0 && (
                    <Flex justify="space-between">
                      <Text fontSize="sm" fontWeight="medium">
                        X Path:
                      </Text>
                      <Code p={1}>{redaction.xPath}</Code>
                    </Flex>
                  )}

                  <Flex justify="space-between">
                    <Text fontSize="sm" fontWeight="medium">
                      Regex:
                    </Text>
                    <Code p={1}>{redaction.regex}</Code>
                  </Flex>
                  {redaction.hash && (
                    <Flex justify="space-between">
                      <Text fontSize="sm" fontWeight="medium">
                        Hash:
                      </Text>
                      <Tag size="sm">{redaction.hash}</Tag>
                    </Flex>
                  )}
                </VStack>
              </Box>
            ))}
          </VStack>
        </Box>
      </VStack>
    </TabPanel>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl">
      <ModalOverlay />
      <ModalContent maxHeight="85vh">
        <ModalCloseButton mt={3} />
        <ModalHeader paddingBottom={0}>
          <Flex direction="column" gap={2}>
            <Flex alignItems="center" justifyContent="flex-start" gap={4}>
              <Image
                height="40px"
                width="40px"
                src={providerDetail?.logoUrl}
                alt="provider logo"
              />
              <Text className="medium-font" fontWeight="bold" textTransform="capitalize">
                {providerDetail?.name}
              </Text>
            </Flex>

            {!providerDetail?.isVerified && (
              <Alert
                borderRadius="lg"
                status="warning"
                flexDirection="column"
                alignItems="flex-start"
                w="max-content"
              >
                <Flex width="100%" alignItems="center">
                  <AlertIcon />
                  <Box flex="1" fontFamily="inter" className="small-font">
                    Provider created by Reclaim Community, Expect Low Stability.
                  </Box>
                  {userType === 'ADMIN' && (
                    <Button
                      isLoading={isLoading}
                      colorScheme="orange"
                      onClick={verifyProvider}
                      size="sm"
                      fontFamily="inter"
                      className="small-font"
                      ml={10}
                    >
                      Verify Provider
                    </Button>
                  )}
                </Flex>
              </Alert>
            )}
          </Flex>
        </ModalHeader>

        <ModalBody overflowY="auto">
          <Box
            mt={pxToRem(8)}
            borderWidth="1px"
            borderRadius="lg"
            borderColor="gray.200"
            overflow="hidden"
            fontFamily="inter"
          >
            <Table variant="simple">
              <Tbody>
                {[
                  {
                    label: 'Name',
                    value: (
                      <Text fontFamily="inter" className="small-font">
                        {providerDetail?.name}
                      </Text>
                    ),
                  },
                  {
                    label: 'Provider Id',
                    value: (
                      <Flex alignItems="center">
                        <Text mr={4} fontFamily="inter" className="small-font">
                          {providerDetail.httpProviderId}
                        </Text>
                        <IconButton
                          variant="ghost"
                          aria-label="Copy provider ID"
                          icon={<CopyIcon />}
                          size="20px"
                          onClick={() => handleCopy(providerDetail.httpProviderId)}
                        />
                      </Flex>
                    ),
                  },
                  {
                    label: 'Description',
                    value: (
                      <Text fontFamily="inter" className="small-font">
                        {providerDetail.description || 'No Description found'}
                      </Text>
                    ),
                  },
                  {
                    label: 'Login URL',
                    value: (
                      <Text
                        wordBreak="break-all"
                        overflowWrap="break-word"
                        fontFamily="inter"
                        className="small-font"
                      >
                        {providerDetail.loginUrl}
                      </Text>
                    ),
                  },
                  {
                    label: 'Verified',
                    value: (
                      <Tag
                        fontFamily="inter"
                        className="small-font"
                        colorScheme={providerDetail?.isVerified ? 'green' : 'red'}
                      >
                        {providerDetail?.isVerified ? 'Verified' : 'Not Verified'}
                      </Tag>
                    ),
                  },
                  {
                    label: 'Custom JS Injections',
                    value: (
                      <Box
                        maxHeight="200px"
                        overflowY="auto"
                        position="relative"
                        fontFamily="inter"
                        className="small-font"
                      >
                        {providerDetail.customInjection && (
                          <IconButton
                            icon={<CopyIcon />}
                            aria-label="Copy custom JS"
                            size="sm"
                            position="absolute"
                            top={1}
                            right={1}
                            zIndex={1}
                            onClick={() =>
                              handleCopy(
                                providerDetail.customInjection ||
                                  'No Custom JS Injections found'
                              )
                            }
                          />
                        )}
                        <Code whiteSpace="pre-wrap" p={2} display="block">
                          {providerDetail.customInjection || (
                            <Text fontFamily="inter" className="small-font">
                              No Custom JS Injections found
                            </Text>
                          )}
                        </Code>
                      </Box>
                    ),
                  },
                  // Android User Agent
                  {
                    label: 'Android User Agent',
                    value: (
                      <Code whiteSpace="pre-wrap" p={2} display="block">
                        {providerDetail?.userAgent?.android || (
                          <Text fontFamily="inter" className="small-font">
                            No Android User Agent Found
                          </Text>
                        )}
                      </Code>
                    ),
                  },
                  // iOS User Agent
                  {
                    label: 'IOS User Agent',
                    value: (
                      <Code whiteSpace="pre-wrap" p={2} display="block">
                        {providerDetail?.userAgent?.ios || (
                          <Text fontFamily="inter" className="small-font">
                            No IOS User Agent Found
                          </Text>
                        )}
                      </Code>
                    ),
                  },
                ].map(({ label, value }, index) => (
                  <Tr key={index}>
                    <Td
                      fontWeight="bold"
                      width="30%"
                      verticalAlign="top"
                      fontFamily="inter"
                      className="small-font"
                    >
                      {label}
                    </Td>
                    <Td>{value}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            <Tabs variant="enclosed" mt={'5'}>
              <TabList>
                {providerDetail?.requestData?.map((_: any, index: number) => (
                  <Tab key={index}>Request {index + 1}</Tab>
                ))}
              </TabList>
              <TabPanels>
                {providerDetail?.requestData?.map((request: any, index: number) =>
                  renderRequestPanel(request, index)
                )}
              </TabPanels>
            </Tabs>
            {providerDetail?.isVerified && userType === 'ADMIN' && (
              <Flex
                w="90%"
                justifyContent="center"
                alignItems="center"
                flexDirection="column"
                gap={4}
                p={4}
                m="auto"
              >
                <Button
                  isLoading={isLoading}
                  colorScheme="red"
                  onClick={unverifyProvider}
                  size="sm"
                  fontFamily="inter"
                  className="small-font"
                >
                  Unverify Provider
                </Button>
              </Flex>
            )}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
